'use client';

import React, { useState, useEffect } from 'react';
import { paths, ROOTS } from "src/routes/paths";

import { useRouter, usePathname } from 'src/routes/hooks';

import { CONFIG } from 'src/global-config';

import { SplashScreen } from 'src/components/loading-screen';

import { useAuthContext } from '../hooks';

// ----------------------------------------------------------------------

type AuthGuardProps = {
  children: React.ReactNode;
};

/**
 * Sidor som inte ska bytas ut mot onboardingen, trots att den inte är klar.
 *
 * Onboardingen själv är grinden, så den måste undantas — annars blir omdirigeringen en loop.
 * Callback-sidorna är det inte lika uppenbart: BankID-legitimeringen sker *inuti* onboardingen
 * och kommer tillbaka hit, och en omdirigering innan sidan hunnit läsa svaret slänger bort
 * legitimeringen. En callback får alltid göra klart och skicka vidare själv.
 */
const UNDANTAGNA_SÖKVÄGAR = [
  ROOTS.ONBOARDING,
  ROOTS.VERIFIERA,
  ROOTS.REGISTRERA,
];

function ärUndantagenFrånOnboarding(pathname: string): boolean {
  return (
    UNDANTAGNA_SÖKVÄGAR.some((sökväg) => pathname.startsWith(sökväg)) ||
    pathname.includes('/callback')
  );
}


export function AuthGuard({ children }: AuthGuardProps) {
  const router = useRouter();
  const pathname = usePathname();

  const { authenticated, loading, user, anvandare } = useAuthContext();

  const [isChecking, setIsChecking] = useState(true);

  const createRedirectPath = (currentPath: string) => {
    const queryString = new URLSearchParams({ returnTo: pathname }).toString();
    return `${currentPath}?${queryString}`;
  };

  const checkPermissions = async (): Promise<void> => {
    if (loading) {
      return;
    }

    if (!authenticated) {
      const signInPath = ROOTS.LOGGA_IN;
      const redirectPath = createRedirectPath(signInPath);

      router.replace(redirectPath);

      return;
    }

    // Bara ett UTTRYCKLIGT false betyder overifierad. Saknas fältet vet vi ingenting, och då
    // ska ingen skickas till verifieringssidan: den lokala providern exponerar attrappen
    // `{ uid: null, email: null }` när ingen är inloggad, och `!user.emailVerified` läste den
    // som en inloggad användare med overifierad adress. Resultatet blev en verifieringssida
    // utan e-postadress, för ett konto som inte ens finns i Firebase.
    if (user && user.emailVerified === false) {
      router.replace(paths.VERIFIERA);
      return;
    }

    // Den som inte gått igenom onboardingen ska dit, oavsett vilken adress hen råkade landa på.
    //
    // Regeln låg tidigare bara i Firebase-providern, så den som loggade in via den lokala
    // providern eller dev-personas hoppade över onboardingen helt och hamnade direkt på
    // översikten. Den hör inte hemma i en inloggningsmekanism: den handlar om användaren, och
    // alla providrar delar den här grinden.
    //
    // Villkoret kräver ett faktiskt användarobjekt. `anvandare` är undefined både under
    // laddning och när hämtningen fallerat, och ett tillfälligt nätverksfel ska inte kasta in
    // någon i onboardingen.
    if (anvandare && !anvandare.harGenomgåttOnboarding && !ärUndantagenFrånOnboarding(pathname)) {
      router.replace(paths.ONBOARDING);
      return;
    }

    setIsChecking(false);
  };

  useEffect(() => {
    checkPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, loading, anvandare, pathname]);

  if (isChecking) {
    return <SplashScreen />;
  }

  return <>{children}</>;
}
